import React from 'react';
import { ArrowRight } from 'lucide-react';
import { PageHero, Reveal, SectionTitle, Btn, Icon, Kicker } from '../components/common';
import { img, missionText, pillars, visionGardens, gardenCollections } from '../mock/mock';

const GardenVision = () => {
  return (
    <div>
      <PageHero image={img.gardenAerial} kicker="Garden Vision" title="A landscape reimagined for South Fulton" sub="Explore the mission, the master conceptual plan, and the gardens taking root today." />

      {/* Mission */}
      <section id="mission" className="py-24 bg-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionTitle kicker="Our Mission" title="Connecting people with nature" />
            <p className="mt-6 text-ink/70 leading-relaxed">{missionText}</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {pillars.map((p) => (
                <div key={p.title} className="flex gap-4 p-4 rounded-xl bg-white border border-sand">
                  <div className="w-11 h-11 rounded-lg bg-forest/8 flex items-center justify-center shrink-0">
                    <Icon name={p.icon} className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <h4 className="font-display text-lg text-forest">{p.title}</h4>
                    <p className="text-xs text-ink/60 mt-1 leading-relaxed">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={150} className="grid grid-cols-2 gap-4">
            <img src={img.woodland} alt="Woodland" className="rounded-2xl h-64 w-full object-cover shadow-lg mt-8" />
            <img src={img.meadow} alt="Meadow" className="rounded-2xl h-64 w-full object-cover shadow-lg" />
            <img src={img.roses} alt="Roses" className="rounded-2xl h-64 w-full object-cover shadow-lg" />
            <img src={img.conservatory} alt="Conservatory" className="rounded-2xl h-64 w-full object-cover shadow-lg mt-8" />
          </Reveal>
        </div>
      </section>

      {/* Master Plan */}
      <section id="master-plan" className="py-24 bg-forest text-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src={img.gardenAerial} alt="Master plan" className="w-full h-[460px] object-cover" />
              </div>
            </Reveal>
            <Reveal delay={150}>
              <SectionTitle light kicker="Master Conceptual Plan" title="A vision drawn from the land itself" />
              <p className="mt-6 text-cream/80 leading-relaxed">
                Developed with leading landscape architects and shaped by our community, the Master Conceptual Plan weaves 40 acres on the historic Camp Truitt property — gardens, woodland, water features and gathering spaces — into a single, walkable journey. Every path is designed to reveal the region's natural beauty and reconnect visitors with the living world.
              </p>
              <ul className="mt-6 space-y-3">
                {['A landmark glass conservatory', 'Fifteen themed display gardens', 'Restored native Piedmont woodland', 'An outdoor amphitheater & event lawns', 'Fully accessible trails throughout'].map((f) => (
                  <li key={f} className="flex items-center gap-3 text-cream/85"><span className="w-1.5 h-1.5 rounded-full bg-gold-light" />{f}</li>
                ))}
              </ul>
              <div className="mt-8"><Btn to="/development">See development progress <ArrowRight className="w-4 h-4" /></Btn></div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Signature gardens */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="Signature Spaces" title="Three landmarks at the heart of the garden" /></Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {visionGardens.map((g, i) => (
              <Reveal key={g.name} delay={i * 120}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:-translate-y-1.5 transition-transform duration-300 h-full">
                  <img src={img[g.img]} alt={g.name} className="w-full h-56 object-cover" />
                  <div className="p-7">
                    <h3 className="font-display text-2xl text-forest">{g.name}</h3>
                    <p className="mt-3 text-ink/65 text-sm leading-relaxed">{g.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gardens grid */}
      <section id="gardens" className="py-24 bg-sand scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle kicker="The Collections" title="Fifteen gardens, endless discovery" sub="A preview of the themed landscapes taking shape across the site." /></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {gardenCollections.map((c, i) => (
              <Reveal key={c.name} delay={(i % 3) * 100}>
                <div className="group relative rounded-2xl overflow-hidden shadow-lg h-72">
                  <img src={img[c.img]} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/90 via-forest-dark/25 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <h3 className="font-display text-xl text-white">{c.name}</h3>
                    <p className="text-cream/80 text-sm mt-2">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GardenVision;
